import { action, extendObservable, toJS } from 'mobx';
import { apiFetchDoc, apiFetchGet, apiFetchPost, apiDownloadNativeDoc } from '../helpers/api';
import {
    getKeyInBytes,
    getIVInBytes,
    encryptNotes,
    decryptNotes,
    decryptDocument,
    decryptOfflineKey,
    getIVInBytesForOfflineKey,
} from '../helpers/decryption';
import {
    SET_NOTES,
    DOC_READ,
    DOC_SHARING,
    GET_NOTES,
    GET_DOCUMENT_TOKEN,
    GET_OFFLINE_DOCUMENT_TOKEN,
    DELETE_DOCUMENT_TOKEN,
    // PDF_CONTENT_TYPE,
    // PDF_TYPE,
    DOC_TYPE_AGENDA,
} from '../helpers/constants';
import {
    ERROR_OPEN_DOCUMENT_MSG,
    ERROR_OPEN_DOCUMENT_TITLE,
    DOC_TOKEN_ERROR_HEADER_MSG,
    DOC_TOKEN_ERROR_CONTENT_MSG,
    DOC_NOTDOWNLOADED_MSG,
    DOC_NOTDOWNLOADED_TITLE,
} from '../helpers/messageText';
import { getNow } from '../helpers/utils';

export default class DocumentStore {
    constructor(factory) {
        this.factory = factory;
        this.currentDocument = null;
        this.cachedAnnotations = {};
        this.documentToken = null;
        this.lastDocumentRequest = null;

        extendObservable(this, {
            isViewerModalOpen: false,
            loadingDocument: null,
            failedParameters: null,
            documentsList: [],
            previousDocuments: [],

            tokenError: false,

            setCurrentDocument: action(document => {
                this.currentDocument = document;
            }),

            clearCurrentDocument: action(() => {
                this.currentDocument = null;
            }),

            openViewerModal: action(() => {
                this.isViewerModalOpen = true;
                // console.log('openViewerModal open ', this.isViewerModalOpen);
            }),

            closeViewerModal: action(page => {
                let historyParams = {
                    instanceId: this.currentDocument.instanceId,
                    docTypeId: this.currentDocument.DocElementType,
                    docId: this.currentDocument.documentId,
                    fileName: this.currentDocument.fileName,
                    extension: this.currentDocument.DocExtension,
                    permissions: this.currentDocument.permissions,
                    page,
                };
                this.registerInPrevious(historyParams);
                this.clearCurrentDocument();
                this.isViewerModalOpen = false;
                // refresh list
                // let location = window.location.pathname;
                // console.log('closeViewerModal closeViewerModal ', location);
                // if (location.includes('agenda')) {
                //     this.factory.meetings.fetch();
                // }
            }),

            registerInPrevious: action(params => {
                if (!this.previousDocuments || !Array.isArray(toJS(this.previousDocuments))) {
                    this.previousDocuments = [];
                }

                let index = this.getIndexInPrevious(params, this.previousDocuments);
                // console.log('registerInPrevious before', toJS(this.previousDocuments));
                // console.log('index', index);
                if (index !== -1) {
                    this.previousDocuments.splice(index, 1);
                }
                this.previousDocuments.push(params);
                // console.log('registerInPrevious after', toJS(this.previousDocuments));
            }),

            getIndexInPrevious(params, prev) {
                return prev.findIndex((el, i) => {
                    return (
                        el.instanceId === params.instanceId &&
                        el.docTypeId === params.docTypeId &&
                        el.docId === params.docId &&
                        el.fileName === params.fileName &&
                        el.extension === params.extension
                    );
                });
            },

            getPreviousDocuments: () => {
                let currentParams = {
                    instanceId: this.currentDocument.instanceId,
                    docTypeId: this.currentDocument.DocElementType,
                    docId: this.currentDocument.documentId,
                    fileName: this.currentDocument.fileName,
                    extension: this.currentDocument.DocExtension,
                    permissions: this.currentDocument.permissions,
                };

                let index = this.getIndexInPrevious(currentParams, this.previousDocuments);

                let allPrev = toJS(this.previousDocuments);
                // console.log('getPreviousDocuments', allPrev);
                if (index !== -1) {
                    allPrev.splice(index, 1);
                    allPrev.push();
                }

                return allPrev;
            },

            //#region WEB APP DOCUMENT TOKEN
            setDocumentToken: async () => {
                return await apiFetchPost(GET_DOCUMENT_TOKEN, this.factory.user.token).then(res => {
                    // console.log('UserStore GET_DOCUMENT_TOKEN', res);
                    if (res && res.Details && res.Details.Token) {
                        this.documentToken = res.Details.Token;
                        this.lastDocumentRequest = Date.now();
                        // localStorage.setItem(local.LOGO_PATH, window.btoa(res.Details.Token));
                        // localStorage.setItem(local.LOGO_TIMESTAMP, Date.now());
                    }
                });
            },

            deleteDocumentToken: async (expireToken = true) => {
                this.documentToken = null;
                if (expireToken) {
                    return await apiFetchPost(DELETE_DOCUMENT_TOKEN, this.factory.user.token);
                }
                return null;
            },
            //#endregion

            //#region ELECTRON DOCUMENT TOKEN (UDK)
            setOfflineDocumentToken: async hashedPin => {
                return await apiFetchPost(GET_OFFLINE_DOCUMENT_TOKEN, this.factory.user.token, {
                    DevicePin: hashedPin,
                }).then(res => {
                    if (res && res.Token) {
                        // store OK token to indexedDB
                        window.offlineApiExtension.saveActor('offlineKey', res.Token);
                        // decrypt token and store UDK to memory
                        factory.user.getValueFromActorStorage('userId').then(userId => {
                            let IV_bytes = getIVInBytesForOfflineKey(hashedPin, userId);
                            this.documentToken = decryptOfflineKey(res.Token, hashedPin, IV_bytes);
                        });
                    }
                });
            },

            async setTempElectronDocumentKey() {
                // TODO get offlineKey and save UDK in the temp memory
                // if it doesn't exists, the app is online and hashed pin exists,
                // get it from the server and save it in the local idb.
                let offlineKey = await factory.user.getValueFromActorStorage('offlineKey');
                let hashedPin = await factory.user.getValueFromActorStorage('hashedPin');
                let userId = await factory.user.getValueFromActorStorage('userId');
                if (offlineKey && hashedPin && userId) {
                    let IV_bytes = getIVInBytesForOfflineKey(hashedPin, userId);
                    this.documentToken = await decryptOfflineKey(offlineKey, hashedPin, IV_bytes);
                    return;
                } else {
                    if (hashedPin && (await this.factory.conn.checkOnlineStatus())) {
                        return await this.setOfflineDocumentToken(hashedPin);
                    }
                }
            },
            //#endregion

            loadingON: action(() => (this.loadingDocument = true)),
            loadingOFF: action(() => (this.loadingDocument = false)),
            tokenErrorON: action(params => {
                this.tokenError = true;
                this.failedParameters = params;

                //show the alert
                this.factory.alert.setAlert(
                    this.updateToken,
                    this.tokenErrorOFF,
                    DOC_TOKEN_ERROR_HEADER_MSG,
                    DOC_TOKEN_ERROR_CONTENT_MSG,
                    'Update',
                    'Cancel',
                );
            }),
            tokenErrorOFF: action(() => {
                this.failedParameters = null;
                this.tokenError = false;
                this.loadingOFF();

                //switch off the alert
                this.factory.alert.hideAlert();
            }),
        });
    }

    updateToken = async () => {
        try {
            // console.log('DocumentStore updateToken');
            await this.setDocumentToken();
            await this.fetchDocument(this.failedParameters);
            this.openViewerModal();
            this.tokenErrorOFF();
        } catch (e) {
            console.log('DocumentStore updateToken error', e);
        }

        //switch off the alert
        this.factory.alert.hideAlert();
    };

    logDocumentRead = async (instanceId, docId, docTypeId) => {
        try {
            let documentParam = {
                instanceId: instanceId,
                docId: docId,
                docTypeId: DOC_TYPE_AGENDA,
                date: '20180304T223852',
            };

            return await this.syncRead(documentParam);
        } catch (e) {
            console.log('DocumentStore logDocumentRead error', e);
        }

        //switch off the alert
        this.factory.alert.hideAlert();
    };

    downloadAndDecryptDocument = async (document) => {
        const rs = await this.downloadDocumentOnly(document)
        const result = rs.arrayBuffer

        const IV_bytes = getIVInBytes(document.docId, document.instanceId, 'pdf')
        const key_bytes = getKeyInBytes()

        return decryptDocument(result, key_bytes, IV_bytes)
    }

    downloadNativeFormatAndDecrypt = async (document) => {
        const result = await apiDownloadNativeDoc(this.factory.user.token, { docId: document.docId, instanceId: document.instanceId, docTypeId: document.docTypeId })

        const IV_bytes = getIVInBytes(document.docId, document.instanceId, document.DocExtension)
        const key_bytes = getKeyInBytes()

        return decryptDocument(result, key_bytes, IV_bytes)
    }

    // DOWNLOADING DOCUMENT ONLY
    /*
     * Fetch document from the server
     * @param instanceId
     * @param docTypeId
     * @param docId
     * @param DocExtension
     * @param DocPermissions
     * @param DocName
     * @param DocAnnotations
     * @returns {Promise.<void>}
     */
    downloadDocumentOnly = async (document, isWorthARead = false) => {
        let {
            instanceId,
            docTypeId,
            docId,
            // DocExtension,
            DocPermissions,
            // DocName,
            // fromHistory,
            // onClose,
        } = document;

        if (!await this.factory.alert.defaultOfflineAlert()) {
            return;
        }

        //this.loadingON();

        // Common consts/variables will be used for any case
        // const docKey = '' + instanceId + docId;

        // const IV_bytes = getIVInBytes(docId, instanceId, DocExtension);
        // let viewableDocumentBytes = null; // bytes can be sent to and viewed in PDFViewer. For WorthARead only, They don't need decryption.
        // let contentType = null;
        // let annotations = null;
        // let key_bytes = null;

        try {
            // check permissions
            if (DocPermissions === 0) {
                throw new Error('You do not have permissions to open this document');
            }

            // Prepare keys
            try {
                // key_bytes = await getKeyInBytes();
                await getKeyInBytes();
            } catch (e) {
                return;
            }

            // let decryptedNoteString;
            try {
                // decrypt DocAnnotations
                let notesParams = {
                    InstanceId: instanceId,
                    DocumentId: docId,
                    DocElementType: docTypeId,
                };

                // Downloading the notes, I do not care about decrypting
                await apiFetchGet(GET_NOTES, this.factory.user.token, notesParams);
            } catch (e) {}

            // put decrypted DocAnnotations to selected doc

            let params = { instanceId, docTypeId, docId };
            let docPromise = apiFetchDoc(this.factory.user.token, params);
            // let res = await apiFetchDoc(this.factory.user.token, params);

            return docPromise;
        } catch (e) {
            const alertTitle =
                e && e.message === DOC_NOTDOWNLOADED_MSG
                    ? DOC_NOTDOWNLOADED_TITLE
                    : ERROR_OPEN_DOCUMENT_TITLE;
            const alertMessage =
                e && e.message === DOC_NOTDOWNLOADED_MSG
                    ? e.message
                    : ERROR_OPEN_DOCUMENT_MSG + ' (Error: ' + (e && e.message) + ')';
            this.factory.alert.setAlert(
                this.factory.alert.hideAlert,
                this.factory.alert.hideAlert,
                alertTitle,
                alertMessage,
                'OK',
                '',
            );
            throw e;
        }

        // store.groups.makeDocumentRead(docId);
    };

    // DOCUMENT DECRYPTION START
    /*
     * Fetch document from the server
     * @param instanceId
     * @param docTypeId
     * @param docId
     * @param DocExtension
     * @param DocPermissions
     * @param DocName
     * @param DocAnnotations
     * @returns {Promise.<void>}
     */
    fetchDocument = async (document, isWorthARead = false) => {
        let {
            instanceId,
            docTypeId,
            docId,
            DocExtension,
            DocPermissions,
            DocName,
            fromHistory,
            onClose,
        } = document;

        if (!window.offlineApiExtension) {
            if (!await this.factory.alert.defaultOfflineAlert()) {
                return;
            }
        }

        this.loadingON();

        // Common consts/variables will be used for any case
        // const docKey = this.buildDocKey(docTypeId, instanceId, docId);

        //IHT July 2018 (BT-1580): The DocExtension needs to be hardcoded to "pdf" unless the original format is being downloaded
        let IV_bytes = getIVInBytes(docId, instanceId, "pdf");

        let viewableDocumentBytes = null; // bytes can be sent to and viewed in PDFViewer. For WorthARead only, They don't need decryption.
        let contentType = null;
        let annotations = null;
        let key_bytes = null;

        try {
            // check permissions
            if (DocPermissions === 0) {
                throw new Error('You do not have permissions to open this document');
            }

            // Prepare keys
            try {
                key_bytes = await getKeyInBytes();
            } catch (e) {
                return;
            }

            let decryptedNoteString;
            try {
                // decrypt DocAnnotations
                let notesParams = {
                    InstanceId: instanceId,
                    DocumentId: docId,
                    DocElementType: docTypeId,
                };

                //sync notes before getting the updates
                if (window.offlineApiExtension) {
                    if (await this.factory.conn.checkOnlineStatus()) {
                        await window.offlineApiExtension.syncNotesInDB(false, null);
                    }
                }

                let notes = await apiFetchGet(GET_NOTES, this.factory.user.token, notesParams);
                if (notes && notes.Result) {
                    decryptedNoteString = decryptNotes(notes.Result, key_bytes, IV_bytes);
                    annotations = JSON.parse(decryptedNoteString);
                }
            } catch (e) {}

            // put decrypted DocAnnotations to selected doc

            let params = { instanceId, docTypeId, docId };
            let res = await apiFetchDoc(this.factory.user.token, params);

            // This should happen only when document has been downloaded and, therefore, idb fails to retrieve document
            if (res == null) {
                // console.log('not downloaded.');
                throw new Error(DOC_NOTDOWNLOADED_MSG);
            } 

            if (!res.contentType || !res.arrayBuffer) {
                throw new Error(res.status);
            }

            contentType = res.contentType;
            var contentTypeArr = contentType.split('/');

            //reset contentType if it is not pdf
            //BT-2051 - if it is returned StatusCode 202 (not 200)
            //and Content Type is jpeg reset to jpg
            if(contentTypeArr[1] !== 'pdf') {
                if(res.status === 202 && contentTypeArr[1] === 'jpeg') {
                    contentTypeArr[1] = 'jpg';
                }

                IV_bytes = getIVInBytes(docId, instanceId, contentTypeArr[1]);
            }           
            
            //console.log("encrypted: store", res.arrayBuffer);

            viewableDocumentBytes = isWorthARead
                ? res.arrayBuffer
                : decryptDocument(res.arrayBuffer, key_bytes, IV_bytes);

            // Encrypt decryptedBytes and save to indexedDB for offline usage
        } catch (e) {
            this.loadingOFF();
            const alertTitle =
                e && e.message === DOC_NOTDOWNLOADED_MSG
                    ? DOC_NOTDOWNLOADED_TITLE
                    : ERROR_OPEN_DOCUMENT_TITLE;
            const alertMessage =
                e && e.message === DOC_NOTDOWNLOADED_MSG
                    ? e.message
                    : ERROR_OPEN_DOCUMENT_MSG + ' (Error: ' + (e && e.message) + ')';
            this.factory.alert.setAlert(
                this.factory.alert.hideAlert,
                this.factory.alert.hideAlert,
                alertTitle,
                alertMessage,
                'OK',
                '',
            );
            throw e;
        }
        // create Blob and return
        let blobObject = new Blob([viewableDocumentBytes], { type: contentType });
        let blobUrl = URL.createObjectURL(blobObject);
        //console.log("viewable :", viewableDocumentBytes);

        // to have res.contentType,

        let doc = {
            blob: blobUrl,
            viewableDocumentBytes,
            contentType: contentType,
            DocExtension,
            fileName: DocName,
            permissions: DocPermissions,
            instanceId,
            DocElementType: docTypeId,
            documentId: docId,
            DocAnnotations: annotations,
            key_bytes,
            IV_bytes,
            fromHistory,
            onClose,
        };
        this.setCurrentDocument(doc);
        this.loadingOFF();
        this.openViewerModal();
        // store.groups.makeDocumentRead(docId);
        return true;
    };

    // API here

    addAnnotation = async annotation => {
        let annotations = this.currentDocument.DocAnnotations || [];
        annotations.push(annotation);
        return this.updateAnnotations(annotations);
    };

    updateAnnotations = async annotations => {
        let { key_bytes, IV_bytes, instanceId, documentId, DocElementType } = this.currentDocument;

        let notes = null;
        let count = null;
        if (annotations && annotations.length > 0) {
            // check the amount of comments

            annotations.forEach(item => {
                if (item.class === 'Comment') count += 1;
            });

            let stringNotes = JSON.stringify(annotations);

            notes = encryptNotes(stringNotes, key_bytes, IV_bytes);
        }

        let params = {
            InstanceId: instanceId,
            DocumentId: documentId,
            DocElementType,
            DocAnnotations: notes,
            DocCommentCount: count,
        };
        // cache decrypted updated notes
        let name = `${instanceId}-${documentId}-${DocElementType}`;
        this.cachedAnnotations[name] = annotations;

        // update notes in the current document
        this.currentDocument.DocAnnotations = annotations;

        // send the notes to the server
        const res = await apiFetchPost(SET_NOTES, this.factory.user.token, params);

        //once notes have been sent, go fetch them in the backgroud to ensure offline db is sync'd
        apiFetchGet(GET_NOTES, this.factory.user.token, params);

        return res;
    };

    // NB: ! used by  electronApiExtension.js !
    ////////////////////////////////////////////////////////////////////////////
    async syncNotesAfterOffline(key, value) {
        let res = await apiFetchPost(SET_NOTES, this.factory.user.token, value);

        //once notes have been sent, go fetch them in the backgroud to ensure offline db is sync'd
        apiFetchGet(GET_NOTES, this.factory.user.token, value);

        return res;
    }

    async syncRead(params) {
        await apiFetchPost(DOC_READ, this.factory.user.token, null, params);
    }

    getCurrentParmasForSharing() {
        const doc = this.currentDocument;
        return {
            instanceId: doc.instanceId,
            docTypeId: doc.DocElementType,
            docId: doc.documentId,
            date: getNow(),
        };
    }

    async syncSharing(params) {
        await apiFetchPost(DOC_SHARING, this.factory.user.token, null, params);
    }

    saveSharing() {
        window.offlineApiExtension.saveSharing(this.getCurrentParmasForSharing());
    }

    buildDocKey(docElementType, instanceId, docId) {
        return '0' + docElementType + '.' + instanceId + '.' + docId;
    }
    buildCompareString(docElementType) {
        return '0' + docElementType + '.';
    }
}



// WEBPACK FOOTER //
// ./src/stores/DocumentStore.js